import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useOffer, useOfferMode } from "@nju/data-access-portal-offers";
import { useOrchestrators } from "@nju/data-access-portal-orchestrator";
import { pushSimpleEvent } from "@nju/data-layer";
import { FAQButton } from "@nju/feature-portal-help";
import { SidebarOffer } from "@nju/feature-portal-sidebar";
import { CancelOrderModal } from "@nju/feature-sales-order";
import { ErrorMessage } from "@nju/form";
import { portalRoutes, salesRoutes } from "@nju/routes";
import { IScope } from "@nju/scope";
import {
  A,
  Button,
  ButtonsWrapper,
  Heading,
  Layout,
  Main,
  Modal,
  Separator,
  Side,
  useOverlay,
} from "@nju/ui";
import { OnboardingMembersInfo } from "../OnboardingMembersInfo";
import {
  ISelectedPostpaidDay,
  PostpaidMigrationForm,
  postpaidMigrationMachine,
} from "./partials";

interface IPostpaidMigrationDatePageProps {
  variant?: IScope;
}

export function PostpaidMigrationDatePage({
  variant = "portal",
}: IPostpaidMigrationDatePageProps) {
  const router = useRouter();
  const cancelOrderModal = useOverlay();
  const mode = useOfferMode();
  const multiOrchestrator = useOrchestrators("multiOrder", {
    isOptional: true,
  });

  if (!multiOrchestrator) {
    throw new Error("Missing orchestrator");
  }

  const isLastNumberOnboarded =
    multiOrchestrator &&
    multiOrchestrator.currentMultiOrderItem ===
      multiOrchestrator.multiOrderItemsCount;

  const { offerId, id } = multiOrchestrator;
  const offer = useOffer(offerId);

  const [{ context, matches }, send] = useMachine(postpaidMigrationMachine, {
    actions: {
      redirectToDeliveryStep: () => {
        if (variant !== "sales") {
          router.push(
            portalRoutes.simCzyEsim.get({
              mode: "mobile",
            }),
          );
        }

        if (variant === "sales") {
          router.push(
            salesRoutes.sposobDostawy.get({
              mode: "mobile",
            }),
          );
        }
      },
      redirectToProceedFurtherStep: () => {
        if (variant !== "sales") {
          router.push(
            portalRoutes.idziemyDalej.get({
              flow: "multiOrder",
              screenVariant: "success",
            }),
          );
        }

        if (variant === "sales") {
          router.push(
            salesRoutes.idziemyDalej.get({
              screenVariant: "success",
            }),
          );
        }
      },
      redirectToPostpaidDayPage: () => {
        if (variant !== "sales") {
          router.push(
            portalRoutes.migracjaNumeru.postpaid.wybierzDateMigracji.get({
              mode: "mobile",
            }),
          );
        }

        if (variant === "sales") {
          router.push(
            salesRoutes.migracjaNumeru.postpaid.wybierzDateMigracji.get({
              mode: "mobile",
            }),
          );
        }
      },
    },
    context: {
      variant,
      orchestratorId: id,
      isLastNumberOnboarded,
    },
  });

  const { errorMessage } = context;

  const handlePostpaidDateSelect = React.useCallback(
    (value: ISelectedPostpaidDay) => {
      send({ type: "SET_POSTPAID_DATE", value });
    },
    [send],
  );

  const handleNextStep = React.useCallback(() => {
    send({ type: "SUBMIT" });
  }, [send]);

  const handlePushDataLayer = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_service",
      eventName: "process_finish_later",
    });
  }, []);

  return (
    <>
      {variant !== "sales" && (
        <OnboardingMembersInfo isMulti={false} multiOrder={multiOrchestrator} />
      )}
      <Layout>
        <Main>
          <Heading
            className="sm:mb-3 mb-4"
            font="ttfirs"
            tone="neutral"
            variant="h2"
            weight="bold"
          >
            {variant !== "sales"
              ? "kiedy chcesz przenieść numer?"
              : "kiedy klient chce przenieść numer?"}
          </Heading>
          <Separator size="s" tone="invisible" />
          <PostpaidMigrationForm
            handlePostpaidDateSelect={handlePostpaidDateSelect}
            variant={variant}
          />
          <FAQButton
            categoryId="NJU_WWW_MNP_PORT_IN_DATE"
            title="co oznaczają obie opcje?"
          />
          {errorMessage && (
            <>
              <Separator size="xxs" tone="invisible" />
              <ErrorMessage message={errorMessage} />
            </>
          )}
          <ButtonsWrapper>
            {variant !== "sales" && (
              <Link
                href={portalRoutes.mojeKonto.index.get({
                  mode,
                  flow: "multiOrder",
                })}
                passHref={true}
              >
                <A
                  onClick={handlePushDataLayer}
                  size={{
                    "@onlyMobile": "s",
                  }}
                  variant="secondary"
                >
                  dokończ później
                </A>
              </Link>
            )}
            {variant === "sales" && (
              <Button
                display="inline"
                onPress={cancelOrderModal.open}
                size={{
                  "@onlyMobile": "s",
                }}
                variant="secondary"
              >
                anuluj
              </Button>
            )}
            <Button
              isLoading={matches("goToNextStep")}
              onPress={handleNextStep}
              size={{
                "@onlyMobile": "s",
              }}
              type="submit"
            >
              dalej
            </Button>
          </ButtonsWrapper>
        </Main>
        <Side>
          <SidebarOffer offer={offer} orchestrator={multiOrchestrator} />
        </Side>
        {cancelOrderModal.isOpened && (
          <Modal onClose={cancelOrderModal.close} title="anulowanie zamówienia">
            <CancelOrderModal
              mode={mode}
              onClose={cancelOrderModal.close}
              orchestratorId={id}
            />
          </Modal>
        )}
      </Layout>
    </>
  );
}
